import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { KalDate, KalDialogRef, KalSnackbarService } from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import { EMPTY, Observable, switchMap } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { UserUpdatePassword } from 'src/app/_user/store/user/user.action';
import { UserState } from 'src/app/_user/store/user/user.state';
import { getISODate } from 'src/app/utils/date.utils';
import {
    ArrayOfUsersDto,
    MutationCryptagePasswordArgs,
    MutationSaveListeUsersGeneriqueArgs,
    StatusDtoEnum
} from 'src/graphql/generated-types';
import { EditPasswordFormValueType } from 'src/app/_shared/components/shared-edit-password/shared-edit-password.component';

export interface EditPasswordDialogData {
    accepted: boolean;
}

@Component({
    selector: 'app-edit-password-dialog',
    templateUrl: './edit-password-dialog.component.html',
    styleUrls: ['./edit-password-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EditPasswordDialogComponent extends BaseComponent {

    formControl = new FormControl<EditPasswordFormValueType | null>(null);

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService,
                private readonly snackbarService: KalSnackbarService,
                private readonly dialogRef: KalDialogRef<EditPasswordDialogComponent>) {
        super(cdr);
    }

    submit(): void {
        if (this.formControl.invalid) {
            return;
        }

        this.watch$ = this.wrapLoader$(
            this.encryptPassword$(this.formControl.value.newPassword).pipe(
                switchMap(encryptedPassword => this.savePassword$(encryptedPassword).pipe(map(() => encryptedPassword))),
                catchError(err => {
                    this.snackbarService.open({title: 'Erreur lors de la modification du mot de passe.'});
                    this.dialogRef.close({accepted: false});
                    return EMPTY;
                }),
                switchMap(encryptedPassword => this.dispatch$(new UserUpdatePassword(encryptedPassword))),
                tap(() => {
                    this.snackbarService.open({title: 'Le mot de passe a bien été modifié.'});
                    this.dialogRef.close({accepted: true});
                })
            ), 'saveNewPassword');
    }

    cancel(): void {
        this.dialogRef.close({accepted: false});
    }

    // Iso g+ mais pas forcément sécurisé :/
    private encryptPassword$(passwordToCrypt: string): Observable<string> {
        return this.graphqlService.mutateOne<'CryptagePassword', MutationCryptagePasswordArgs>({
            mutation: gql`
                mutation CryptagePassword(
                    $contexte: ContextInfosInput
                    $passwordToCrypt: String
                ) {
                    CryptagePassword(
                        contexte: $contexte
                        passwordToCrypt: $passwordToCrypt
                    )
                }
            `,
            variables: {
                passwordToCrypt
            }
        });
    }

    private savePassword$(newEncryptedPassword: string): Observable<ArrayOfUsersDto> {
        return this.graphqlService.mutateOne<'SaveListeUsersGenerique', MutationSaveListeUsersGeneriqueArgs>({
            mutation: gql`
                mutation SaveListeUsersGenerique(
                    $contexte: ContextInfosInput
                    $liste: ArrayOfUsersDTOInput
                    $critere: CriteresUsersDTOInput
                ) {
                    SaveListeUsersGenerique(
                        contexte: $contexte
                        liste: $liste
                        critere:$critere
                    ){
                        UsersDTO{
                            Id
                        }
                    }
                }`,
            variables: {
                critere: {},
                liste: {
                    UsersDTO: [{
                        ...this.snapshot(UserState.connectedUser),
                        Password: newEncryptedPassword,
                        HasMDPSecurise: true,
                        DateUpdateMDP: getISODate(new KalDate()),
                        Status: StatusDtoEnum.Updated
                    }]
                }
            }
        });
    }

}
